import {
    Component,
    ContentChild,
    HostListener,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    TemplateRef,
} from '@angular/core';
import {ActivitiesService} from '@modules/activities/core/activities.service';
import {ActivityGranule} from '@modules/activities/core/models';
import {BehaviorSubject, Observable, ReplaySubject, scan, Subject, Subscription} from 'rxjs';
import {debounceTime, map, startWith, take, takeUntil} from 'rxjs/operators';
import {ActivitySearchFilters, ActivitySearchOptions} from '../activity-search-options';

const ACTIVITIES_PER_PAGE = 12;
const MINIMUM_ACTIVITY_THRESHOLD_BEFORE_AUTO_LOAD = 5;

@Component({
    selector: 'app-activities-search-viewer',
    templateUrl: './activities-search-viewer.component.html',
    styleUrls: ['./activities-search-viewer.component.scss'],
    standalone: false
})
export class ActivitiesSearchViewerComponent implements OnChanges, OnDestroy, OnInit {

    @Input() activitySearchOptions!: ActivitySearchOptions;
    @ContentChild(TemplateRef) templateRef: TemplateRef<any>;

    public isLoading = false;
    public hasLoadError = false;
    private activities$: BehaviorSubject<ActivityGranule[] | null> = new BehaviorSubject<ActivityGranule[] | null>(null);
    public activitiesAcc$ = this.activities$.pipe(
        scan((acc, value) => value === null ? [] : [...acc, ...value], [] as ActivityGranule[]),
    );

    private readonly activitiesService = inject(ActivitiesService);
    private hasNextPage = true;
    private pageNumber = 1;
    private readonly isShouldLoadNextPage$ = new ReplaySubject<void>(1);
    private readonly destroy$ = new Subject<void>();
    private activitiesSubscription?: Subscription;
    private isInitialized = false;

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.isInitialized || changes['activitySearchOptions']?.firstChange !== false) {
            return;
        }

        this.resetSearch();
    }

    ngOnInit() {
        this.subscribeToLoadNextPage();
        this.isInitialized = true;
    }

    ngOnDestroy(): void {
        this.activitiesSubscription?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    @HostListener('scroll', ['$event'])
    onScroll(event: Event) {
        if (!this.isLoading && this.isNearEndOfScroll(event)) {
            this.isShouldLoadNextPage$.next();
        }
    }

    private subscribeToLoadNextPage() {
        this.isShouldLoadNextPage$.pipe(
            startWith(null),
            debounceTime(500),
            takeUntil(this.destroy$),
        ).subscribe(() => this.loadNextPage());
    }

    private isNearEndOfScroll(event: Event) {
        const element = event.target as HTMLElement;
        const scrollPosition = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;
        const limitMargin = clientHeight;

        const nearEndThreshold = scrollHeight - clientHeight;
        const triggerThreshold = nearEndThreshold - limitMargin;

        return scrollPosition >= triggerThreshold;
    }

    private loadNextPage() {
        if (this.isLoading || this.hasLoadError || !this.hasNextPage) {
            return;
        }

        this.isLoading = true;
        const filters = this.generateFilters();
        const requestVersion = this.pageNumber;
        this.subscribeToActivitiesObservable(this.getPaginatedActivities(filters, requestVersion));
    }

    private subscribeToActivitiesObservable(activitiesObservable: Observable<{ entities: ActivityGranule[], hasNextPage: boolean }>) {
        this.activitiesSubscription?.unsubscribe();
        this.activitiesSubscription = activitiesObservable.pipe(
            take(1),
            takeUntil(this.destroy$),
        ).subscribe({
            next: ({entities, hasNextPage}) => {
                this.activities$.next(entities.slice());
                this.isLoading = false;
                this.updatePageState(hasNextPage, entities);
            },
            error: () => {
                this.isLoading = false;
                this.hasLoadError = true;
            },
        });
    }

    private updatePageState(hasNextPage: boolean, currentActivities: ActivityGranule[]) {
        this.hasNextPage = hasNextPage;

        if (hasNextPage) {
            this.pageNumber++;
        }

        if (currentActivities.length <= MINIMUM_ACTIVITY_THRESHOLD_BEFORE_AUTO_LOAD && this.hasNextPage) {
            this.loadNextPage();
        }
    }

    private getPaginatedActivities(filters: ActivitySearchFilters, pageNumber: number) {
        return this.activitiesService.loadPaginatedActivities({
            filter: filters,
            range: ACTIVITIES_PER_PAGE,
            page: pageNumber
        }, 'basic_search').pipe(
            map(({entities, paginator}) => ({
                entities,
                hasNextPage: paginator.hasNextPage
            }))
        );
    }

    private generateFilters(): ActivitySearchFilters {
        const filters: Record<string, string | number | readonly (string | number)[]> = {
            ...(this.activitySearchOptions?.filters ?? {}),
        };

        if (this.activitySearchOptions?.concepts?.length) {
            filters['concepts'] = this.activitySearchOptions.concepts.map(concept => concept.id);
        }
        if (this.activitySearchOptions?.educationalLevels?.length) {
            filters['level'] = this.activitySearchOptions.educationalLevels.map(level => level.id);
        }
        if (this.activitySearchOptions?.chapters?.length) {
            filters['chapters'] = this.activitySearchOptions.chapters.map(chapter => chapter.id);
        }

        return filters;
    }

    private resetSearch(): void {
        this.activitiesSubscription?.unsubscribe();
        this.activities$.next(null);
        this.hasNextPage = true;
        this.pageNumber = 1;
        this.hasLoadError = false;
        this.isLoading = false;
        this.isShouldLoadNextPage$.next();
    }

    public retryLoading(): void {
        this.hasLoadError = false;
        this.isShouldLoadNextPage$.next();
    }

    public hasMorePageToLoad() {
        return this.hasNextPage;
    }
}
